// ──────────────────────────── Demande Absence Status ────────────────────────────
// Canonical status config (plan-business-semantic §2.5.2, Shape C collapse).
//
// Collapses the former double status structure of config/workflow.config.ts
// (two maps holding the same data: one label + daisyUI-class map, one duplicated
// state map) into a single STATUS map driving the shared StatusBadge via L2.5
// semantic tokens.
//
// IMPORTANT: `code` must match the backend EtatDemande enum exactly (fixture
// EtatDemandeFixture): SOUMISE, EN_VALIDATION, APPROUVEE, REFUSEE, ANNULEE.
// BROUILLON is UI-only legacy — the backend creates requests directly in SOUMISE
// (DemandeAbsenceWorkflowService: "no BROUILLON state anymore"), but the form
// still previews it as the HR-mode state.

import type { StatusToken } from "@lib/config/statusTokens";
import {
  Ban,
  CheckCircle,
  Clock,
  FilePen,
  Hourglass,
  type LucideIcon,
  XCircle,
} from "lucide-react";

export interface StatusConfig {
  readonly code: string;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
}

/** Backend EtatDemande code → display config (canonical shape §2.5.2). */
export const STATUS = {
  BROUILLON: {
    code: "BROUILLON",
    label: "Brouillon",
    token: "draft",
    icon: FilePen,
  },
  SOUMISE: {
    code: "SOUMISE",
    label: "Soumise",
    token: "pending",
    icon: Clock,
  },
  EN_VALIDATION: {
    code: "EN_VALIDATION",
    label: "En validation",
    token: "awaiting-approval",
    icon: Hourglass,
  },
  APPROUVEE: {
    code: "APPROUVEE",
    label: "Approuvée",
    token: "completed",
    icon: CheckCircle,
  },
  REFUSEE: {
    code: "REFUSEE",
    label: "Refusée",
    token: "rejected",
    icon: XCircle,
  },
  ANNULEE: {
    code: "ANNULEE",
    label: "Annulée",
    token: "cancelled",
    icon: Ban,
  },
} as const satisfies Record<string, StatusConfig>;

export type StatusCode = keyof typeof STATUS;

/** Valid state transitions (semantics preserved from the legacy workflow transitions). */
export const STATUS_TRANSITIONS: Record<StatusCode, readonly StatusCode[]> = {
  BROUILLON: ["SOUMISE", "ANNULEE"],
  SOUMISE: ["EN_VALIDATION", "ANNULEE"],
  EN_VALIDATION: ["APPROUVEE", "REFUSEE"],
  APPROUVEE: [],
  REFUSEE: [],
  ANNULEE: [],
};

/** States that can still be edited (draft). */
export const EDITABLE_STATES: readonly StatusCode[] = ["BROUILLON"];

/**
 * Maps a backend EtatDemande code to a StatusCode.
 * - Unknown/null/undefined codes fall back to BROUILLON (neutral draft),
 *   mirroring the legacy fallback behavior.
 */
export function resolveStatus(code: string | null | undefined): StatusCode {
  if (code && code in STATUS) return code as StatusCode;
  return "BROUILLON";
}

/** Returns the display config for a backend code (never throws). */
export function getStatusConfig(code: string | null | undefined): StatusConfig {
  return STATUS[resolveStatus(code)];
}

/** Filter dropdown options, in STATUS declaration order. */
export function toStatusOptions(): { value: string; label: string }[] {
  return Object.values(STATUS).map((config) => ({ value: config.code, label: config.label }));
}
